import React, { useState, useRef } from 'react';
import clsx from 'clsx';
import { Col, Container, Divider, Row, Space, Typography } from '@ag1/ui';
import {
  ImageAndExpandableListV2 as ImageAndExpandableListProps,
  SanityKeyed
} from 'types/studio/studio';
import { CustomPortableText } from 'shared/studio/PortableText';
import { navigationClickEvent } from 'analytics/events/studio';
import { StudioHotspotImageV2 } from '../StudioHotspotImage';
import Heading from './Heading/Heading';
import ExpandableList from './ExpandableList/ExpandableList';
import TableSection from './TableSection/TableSection';
import NumbersSection from './NumbersSection/NumbersSection';
import Badges from './Badges/Badges';
import SectionButton from './SectionButton/SectionButton';
import styles from './ImageAndExpandableListV2.module.scss';
import SubstancePopup from './SubstancePopup/SubstancePopup';
import { ScrollableImage } from './ScrollableImage';
import { CommonSectionProps } from '../types';

const ImageAndExpandableListV2 = ({
  _type: sectionType,
  _key: key,
  image,
  mobileImage,
  contentPosition,
  headline,
  subtitle,
  expandableList = [],
  popup,
  tableOptions,
  numbersOptions,
  badgesOptions,
  topButtonOptions,
  mainButtonOptions,
  theme,
  visibleOnPageLoad,
  footNote
}: SanityKeyed<ImageAndExpandableListProps> & CommonSectionProps) => {
  const contentLeft = contentPosition === 'left';
  const [isPopupVisible, setIsPopupVisible] = useState(false);

  const sectionRef = useRef<HTMLDivElement>(null);

  const sendNavigationEvent = (listHeadline: string) => {
    navigationClickEvent({
      location: sectionType,
      navigationType: 'accordion',
      clickText: 'expand',
      header: headline,
      subheader: listHeadline
    });
  };

  return (
    <div id={key} className={styles.root} ref={sectionRef}>
      <Container narrow>
        <Row className={styles.row} align="between">
          <Col span={{ xxs: 12, m: 6 }} className={styles.imageColumn}>
            <ScrollableImage
              sectionRef={sectionRef}
              contentLeft={contentLeft}
              image={image}
              visibleOnPageLoad={visibleOnPageLoad}
            />
            <div className={styles.hotspotImageContainer}>
              <StudioHotspotImageV2
                image={mobileImage?.asset ? mobileImage : image}
                className={styles.hotspotIllustration}
                sizes={{ xxs: 12, m: 6 }}
                eagerLoading={visibleOnPageLoad}
              />
            </div>
          </Col>
          <Col
            span={{ xxs: 12, m: 6 }}
            className={clsx(styles.contentColumn, contentLeft && styles.contentAtLeft)}>
            <Space
              top={{ xxs: 40, s: 36, m: 80, l: 100, xxl: 150 }}
              bottom={{ xxs: 40, s: 60, m: 80, l: 100, xxl: 150 }}
              right={{
                l: contentLeft ? 80 : 0,
                xxl: contentLeft ? 84 : 0
              }}
              left={{
                l: !contentLeft ? 80 : 0,
                xxl: !contentLeft ? 84 : 0
              }}>
              <div>
                <Heading headline={headline} subtitle={subtitle} theme={theme} />
                <SectionButton
                  sectionType={sectionType}
                  buttonOptions={topButtonOptions}
                  theme={theme}
                />
                <Divider className={styles.divider} />
                <ExpandableList
                  popupIsActive={!!popup?.isActive}
                  openPopup={() => setIsPopupVisible(true)}
                  expandableList={expandableList}
                  sendNavigationEvent={sendNavigationEvent}
                  theme={theme}
                />
                <TableSection tableOptions={tableOptions} theme={theme} />
                <NumbersSection numbersOptions={numbersOptions} theme={theme} />
                <Badges badgesOptions={badgesOptions} />
                <SectionButton
                  isMainButton
                  sectionType={sectionType}
                  buttonOptions={mainButtonOptions}
                  theme={theme}
                />
                {footNote ? (
                  <Typography
                    variant="portalCaption"
                    size="2"
                    color="grey-1-new"
                    noPadding
                    className={styles.footnote}>
                    <CustomPortableText value={footNote} />
                  </Typography>
                ) : (
                  ''
                )}
              </div>
            </Space>
          </Col>
        </Row>
      </Container>
      {popup?.isActive && (
        <SubstancePopup
          popup={popup}
          popupVisibility={isPopupVisible}
          closePopup={() => setIsPopupVisible(false)}
          theme={theme}
        />
      )}
    </div>
  );
};

export default ImageAndExpandableListV2;
